import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, superadminAPI } from '@/lib/api';
import { getTokenExpiryMs, isTokenExpired } from '@/lib/jwt';
import { getAssertion } from '@/lib/webauthn';

const AuthContext = createContext(null);

// Tolerate ~1 min of client-clock drift so we never proactively log out a
// session that is still valid on the server.
const EXPIRY_SKEW_MS = 60 * 1000;
// setTimeout fires immediately for delays above this 32-bit ceiling.
const MAX_TIMEOUT_MS = 2_147_483_647;

// Single source of truth for wiping the persisted session.
function clearStoredSession() {
  localStorage.removeItem('eva_token');
  localStorage.removeItem('eva_user');
  localStorage.removeItem('eva_tenant');
  localStorage.removeItem('eva_viewing_as_superadmin');
}

// Permission constants
export const PERMISSIONS = {
  KB_VIEW: 'kb_view',
  KB_MANAGE: 'kb_manage',
  KB_UPLOAD: 'kb_upload',
  KB_APPROVE: 'kb_approve',
  KB_PUBLISH: 'kb_publish',
  KB_DELETE: 'kb_delete',
  TEAM_MANAGE: 'team_manage',
  REPORTS_EXPORT: 'reports_export',
};

// Role permission mappings
const ROLE_PERMISSIONS = {
  admin: [
    PERMISSIONS.KB_VIEW,
    PERMISSIONS.KB_MANAGE,
    PERMISSIONS.KB_UPLOAD,
    PERMISSIONS.KB_APPROVE,
    PERMISSIONS.KB_PUBLISH,
    PERMISSIONS.KB_DELETE,
    PERMISSIONS.TEAM_MANAGE,
    PERMISSIONS.REPORTS_EXPORT,
  ],
  approver: [
    PERMISSIONS.KB_VIEW,
    PERMISSIONS.KB_MANAGE,
    PERMISSIONS.KB_UPLOAD,
    PERMISSIONS.KB_APPROVE,
    PERMISSIONS.KB_PUBLISH,
    PERMISSIONS.REPORTS_EXPORT,
  ],
  analyst: [
    PERMISSIONS.KB_VIEW,
    PERMISSIONS.KB_MANAGE,
    PERMISSIONS.KB_UPLOAD,
    PERMISSIONS.REPORTS_EXPORT,
  ],
  viewer: [
    PERMISSIONS.KB_VIEW,
  ],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [viewingAsSuperadmin, setViewingAsSuperadmin] = useState(false);
  const [homeTenantId, setHomeTenantId] = useState(null);

  // Initialize auth state from localStorage - intentional setState on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('eva_token');
    const savedUser = localStorage.getItem('eva_user');
    const savedTenant = localStorage.getItem('eva_tenant');
    const savedViewingAs = localStorage.getItem('eva_viewing_as_superadmin');

    // Session timed out while the tab was closed: don't restore a dead token,
    // so the user lands cleanly logged out instead of seeing an authed UI that
    // 401s on the first action.
    if (isTokenExpired(savedToken, EXPIRY_SKEW_MS)) {
      clearStoredSession();
      setLoading(false);
      return;
    }

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsSuperadmin(parsedUser.is_superadmin || false);
        setHomeTenantId(parsedUser.home_tenant_id || parsedUser.tenant_id);
        if (savedTenant) {
          const parsedTenant = JSON.parse(savedTenant);
          setTenant(parsedTenant);
          // Check if we're viewing a different tenant than our home tenant
          if (parsedUser.is_superadmin && savedViewingAs === 'true') {
            setViewingAsSuperadmin(true);
          }
        }
      } catch (_e) {
        // A corrupt eva_user/eva_tenant value (e.g. the literal string
        // "undefined" from an earlier JSON.stringify(undefined) bug) must not
        // be left half-applied: token would stay set while user stays null,
        // which ProtectedRoute reads as logged-out forever with no way to
        // self-heal short of manually clearing storage. Clear the whole
        // session instead so the user just sees a clean logged-out state.
        console.error('Failed to parse saved user/tenant -- clearing stored session');
        clearStoredSession();
        setToken(null);
        setUser(null);
        setTenant(null);
      }
    }
    setLoading(false);
  }, []);

  // Force-logout when the access token has timed out. Hard-redirect (matching
  // api.js / ErrorPage.js) so the whole app re-initialises in a clean
  // logged-out state; ?expired=1 lets LoginPage explain what happened.
  const expireSession = useCallback(() => {
    clearStoredSession();
    setToken(null);
    setUser(null);
    setTenant(null);
    setIsSuperadmin(false);
    setViewingAsSuperadmin(false);
    setHomeTenantId(null);
    window.location.href = '/login?expired=1';
  }, []);

  // Proactively log out exactly when the token lapses, so a long-idle tab
  // doesn't let the user start work that will 401 on submit.
  useEffect(() => {
    if (!token) return undefined;
    const expiryMs = getTokenExpiryMs(token);
    if (expiryMs === null) return undefined; // unparseable → rely on 401 backstop
    const delay = Math.min(Math.max(expiryMs + EXPIRY_SKEW_MS - Date.now(), 0), MAX_TIMEOUT_MS);
    const timerId = setTimeout(expireSession, delay);
    return () => clearTimeout(timerId);
  }, [token, expireSession]);

  // A tab left open overnight gets no timer event until it's focused again, so
  // re-check the moment it becomes visible / regains focus — this is what
  // catches the "left it open overnight" case before any interaction.
  useEffect(() => {
    const checkExpiry = () => {
      if (isTokenExpired(localStorage.getItem('eva_token'), EXPIRY_SKEW_MS)) {
        expireSession();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkExpiry();
    };
    window.addEventListener('focus', checkExpiry);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', checkExpiry);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [expireSession]);

  // Memoised so callers can safely list it in useCallback/useEffect dependency
  // arrays. The OAuth callbacks in LoginPage are memoised effects — an unstable
  // setSession there would re-create the callback every render and re-fire the
  // effect in a loop. Only setState functions are used inside, and those are
  // stable, so an empty dependency list is correct.
  const setSession = useCallback((data) => {
    localStorage.setItem('eva_token', data.access_token);
    localStorage.setItem('eva_user', JSON.stringify(data.user));
    if (data.tenant) {
      localStorage.setItem('eva_tenant', JSON.stringify(data.tenant));
      setTenant(data.tenant);
    }
    localStorage.removeItem('eva_viewing_as_superadmin');
    setViewingAsSuperadmin(false);
    setToken(data.access_token);
    setUser(data.user);
    setIsSuperadmin(data.user.is_superadmin || false);
    setHomeTenantId(data.user.home_tenant_id || data.user.tenant_id);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((current) => {
      if (!current) return current;
      const updated = { ...current, ...updates };
      localStorage.setItem('eva_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const login = async (email, password) => {
    const data = await authAPI.login(email, password);

    // /auth/login is discriminated since EIP-319: for a superadmin with passkey
    // 2FA latched it returns a step-up challenge (HTTP 200) rather than a
    // session. Branch BEFORE setSession — that call writes
    // `data.access_token` unconditionally, so a step-up response would store
    // `eva_token: "undefined"` and leave the app looking half-authenticated.
    //
    // Nothing is persisted for the step-up phase. The token stays in React
    // state on the login page and never reaches localStorage.
    if (data?.status === 'step_up_required') {
      return data;
    }

    setSession(data);
    return data;
  };

  // Completes the passkey half of a superadmin login. Kept separate from
  // `login` so that every localStorage write still happens in exactly one
  // place (setSession), rather than being duplicated across two entry points.
  //
  // EIP-571: split in two on purpose. This used to await stepUpOptions and then
  // call navigator.credentials.get(), which works on desktop Chrome and fails
  // on iOS Safari: the transient user activation from the click is spent by the
  // network round trip, and WebAuthn is then refused with NotAllowedError,
  // surfacing as "Passkey prompt was cancelled or timed out". The options fetch
  // is not cheap either -- two DB purges, a fetch, an insert and a bind.
  //
  // The caller now fetches options up front and calls this from the click
  // handler, so getAssertion runs synchronously inside the user gesture.
  const fetchStepUpOptions = async (stepUpToken) => {
    const { options, challenge_id: challengeId } = await authAPI.stepUpOptions(stepUpToken);
    return { options, challengeId };
  };

  const completeStepUp = async (stepUpToken, options, challengeId) => {
    const credential = await getAssertion(options);
    const data = await authAPI.stepUpVerify(stepUpToken, credential, challengeId);
    setSession(data);
    return data;
  };

  // Retained for callers that are not in a user gesture; still ordered the old
  // way and therefore still subject to the iOS behaviour above.
  const loginWithStepUp = async (stepUpToken) => {
    const { options, challengeId } = await fetchStepUpOptions(stepUpToken);
    return completeStepUp(stepUpToken, options, challengeId);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      console.error('Logout error:', e);
    }
    clearStoredSession();
    setToken(null);
    setUser(null);
    setTenant(null);
    setIsSuperadmin(false);
    setViewingAsSuperadmin(false);
    setHomeTenantId(null);
  };

  // Switch to a different tenant (superadmin only)
  const switchTenant = useCallback(async (tenantId) => {
    if (!isSuperadmin) return;
    
    const response = await superadminAPI.switchTenant(tenantId);
    
    // Update local state with full branding (logos + icons) and subscription info
    const t = response.tenant;
    const newTenant = {
      tenant_id: t.tenant_id || t.id,
      shortname: t.shortname,
      display_name: t.display_name,
      primary_color: t.primary_color,
      secondary_color: t.secondary_color,
      logo_url: t.logo_url,
      logo_url_light: t.logo_url_light,
      logo_url_dark: t.logo_url_dark,
      icon_url: t.icon_url,
      icon_url_light: t.icon_url_light,
      icon_url_dark: t.icon_url_dark,
      // Include subscription/trial data to update banner state
      subscription_status: t.subscription_status,
      trial_days_remaining: t.trial_days_remaining,
      trial_expired: t.trial_expired,
      trial_ends_at: t.trial_ends_at,
      // EIP-322. This whitelist is why paid expiry was invisible to the client: the
      // field simply never crossed the boundary, so no amount of frontend logic could
      // have surfaced it.
      subscription_state: t.subscription_state,
      subscription_grace_ends_at: t.subscription_grace_ends_at,
      subscription_days_remaining: t.subscription_days_remaining,
    };
    
    const isViewingOther = tenantId !== homeTenantId;
    setTenant(newTenant);
    setViewingAsSuperadmin(isViewingOther);
    localStorage.setItem('eva_tenant', JSON.stringify(newTenant));
    localStorage.setItem('eva_viewing_as_superadmin', isViewingOther ? 'true' : 'false');
    
    // Update user object with new tenant_id
    if (user) {
      const updatedUser = { ...user, tenant_id: tenantId, tenant: newTenant };
      setUser(updatedUser);
      localStorage.setItem('eva_user', JSON.stringify(updatedUser));
    }
    
    return response;
  }, [isSuperadmin, homeTenantId, user]);

  // Return to home tenant (superadmin only)
  const returnHome = useCallback(async () => {
    if (!isSuperadmin) return;
    
    const response = await superadminAPI.returnHome();
    
    const rh = response.tenant;
    const newTenant = {
      tenant_id: rh.tenant_id || rh.id,
      shortname: rh.shortname,
      display_name: rh.display_name,
      primary_color: rh.primary_color,
      secondary_color: rh.secondary_color,
      logo_url: rh.logo_url,
      logo_url_light: rh.logo_url_light,
      logo_url_dark: rh.logo_url_dark,
      icon_url: rh.icon_url,
      icon_url_light: rh.icon_url_light,
      icon_url_dark: rh.icon_url_dark,
    };
    
    setTenant(newTenant);
    setViewingAsSuperadmin(false);
    localStorage.setItem('eva_tenant', JSON.stringify(newTenant));
    localStorage.setItem('eva_viewing_as_superadmin', 'false');
    
    if (user) {
      const updatedUser = { ...user, tenant_id: response.tenant_id, tenant: newTenant };
      setUser(updatedUser);
      localStorage.setItem('eva_user', JSON.stringify(updatedUser));
    }
    
    return response;
  }, [isSuperadmin, user]);

  // Refresh tenant data (useful after subscription changes)
  const refreshTenant = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      if (response.tenant) {
        const updatedTenant = {
          ...tenant,
          ...response.tenant,
          subscription_status: response.tenant.subscription_status,
          trial_days_remaining: response.tenant.trial_days_remaining,
          trial_ends_at: response.tenant.trial_ends_at,
          // Second whitelist — the refresh path. Both have to carry these or a renewal
          // leaves the banner up until the user logs out (EIP-322).
          subscription_state: response.tenant.subscription_state,
          subscription_grace_ends_at: response.tenant.subscription_grace_ends_at,
          subscription_days_remaining: response.tenant.subscription_days_remaining,
        };
        setTenant(updatedTenant);
        localStorage.setItem('eva_tenant', JSON.stringify(updatedTenant));
      }
      return response;
    } catch (err) {
      console.error('Failed to refresh tenant:', err);
    }
  }, [tenant]);

  // Re-read the current user from the server and update the cached copy.
  //
  // The user payload in localStorage is written once at sign-in, so anything
  // derived from it goes stale until the next login — including the passkey
  // credential count the enrollment gate reads (EIP-305). refreshTenant above
  // deliberately merges only the tenant slice and drops response.user, so it
  // cannot serve this purpose.
  // GET /auth/me returns UserResponse, whose fields sit at the TOP level with
  // only `tenant` nested — unlike POST /auth/login, which nests the user under
  // `user`. This was written against the login shape and pointed at /auth/me,
  // so `response.user` was always undefined and every call returned null
  // without throwing: no state written, nothing logged. Silent since EIP-305a.
  // refreshTenant above reads `response.tenant` and works, which is the proof.
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      // Guard on a scalar that actually exists on the payload. `tenant` is
      // dropped here because refreshTenant owns it and merging it would
      // duplicate the whole blob into eva_user.
      if (!response?.id) return null;
      const { tenant: _tenant, ...userFields } = response;

      setUser((current) => {
        const updated = { ...(current || {}), ...userFields };
        localStorage.setItem('eva_user', JSON.stringify(updated));
        return updated;
      });
      setIsSuperadmin(userFields.is_superadmin || false);
      return userFields;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return null;
    }
  }, []);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!user?.platform_role) return false;
    // Superadmins viewing other tenants get full admin access
    if (isSuperadmin && viewingAsSuperadmin) return true;
    const rolePerms = ROLE_PERMISSIONS[user.platform_role] || [];
    return rolePerms.includes(permission);
  }, [user, isSuperadmin, viewingAsSuperadmin]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissions) => {
    return permissions.some(perm => hasPermission(perm));
  }, [hasPermission]);

  // Check if user's role is one of the allowed roles
  const hasRole = useCallback((allowedRoles) => {
    if (!user?.platform_role) return false;
    // Superadmins viewing other tenants are treated as admins
    if (isSuperadmin && viewingAsSuperadmin) return allowedRoles.includes('admin');
    return allowedRoles.includes(user.platform_role);
  }, [user, isSuperadmin, viewingAsSuperadmin]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token,
      tenant,
      loading, 
      login,
      loginWithStepUp,
      fetchStepUpOptions,
      completeStepUp,
      setSession,
      updateUser,
      logout,
      hasPermission,
      hasAnyPermission,
      hasRole,
      platformRole: user?.platform_role || 'viewer',
      // Superadmin features
      isSuperadmin,
      viewingAsSuperadmin,
      homeTenantId,
      switchTenant,
      returnHome,
      refreshTenant,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
